import React from 'react';
import loadingIcon from 'src/assets/loading.gif';
import throttle from 'lodash/throttle';
import './style.scoped.css';

export enum ButtonTypes {
  PrimaryFilled = 'primaryFilled',
  PrimaryOutlined = 'primaryOutlined',
  PrimaryBorderless = 'primaryBorderless',
  PrimaryBorderless2 = 'primaryBorderless2',
  PrimaryBorderless3 = 'primaryBorderless3',
  SecondaryFilled = 'secondaryFilled',
  SecondaryOutlined = 'secondaryOutlined',
  SecondaryBorderless = 'secondaryBorderless',
  ErrorBorderless = 'errorBorderless',
  OutlinedNeutral = 'outlinedNeutral',
}

export enum ButtonSizes {
  Small = 'small',
  Normal = 'normal',
}

type ButtonProps = {
  text: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (data?: any) => void;
  buttonType?: ButtonTypes;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: string;
  extraClassName?: string;
  [propertyName: string]: any;
};

const Button = (props: ButtonProps) => {
  const {
    text,
    loading = false,
    onClick,
    buttonType = ButtonTypes.PrimaryFilled,
    disabled = false,
    leftIcon,
    rightIcon,
    size = 'normal',
    extraClassName,
    ...passProps
  } = props;

  const _handleClick = throttle((event: React.MouseEvent) => {
    if (loading || disabled) return;
    !!onClick && onClick(event);
  }, 500);

  const _getButtonBackgroundClassName = (): string => {
    switch (buttonType) {
      case ButtonTypes.SecondaryFilled:
        return 'bg-secondary';
      case ButtonTypes.PrimaryOutlined:
      case ButtonTypes.PrimaryBorderless:
      case ButtonTypes.SecondaryOutlined:
      case ButtonTypes.SecondaryBorderless:
        return 'bg-surface';
      case ButtonTypes.PrimaryBorderless2:
        return 'bg-primaryLight';
      case ButtonTypes.PrimaryBorderless3:
        return 'bg-primaryLight2';
      case ButtonTypes.OutlinedNeutral:
        return 'bg-background';
      case ButtonTypes.ErrorBorderless:
        return 'bg-errorLight';
      default:
        return 'bg-primary';
    }
  };

  const _getButtonBorderClassName = (): string => {
    switch (buttonType) {
      case ButtonTypes.PrimaryOutlined:
        return 'border border-primary';
      case ButtonTypes.SecondaryOutlined:
        return 'border border-secondary';
      case ButtonTypes.OutlinedNeutral:
        return 'border border-line';
      default:
        return 'border border-transparent';
    }
  };

  const _getButtonTextClassName = (): string => {
    switch (buttonType) {
      case ButtonTypes.PrimaryOutlined:
      case ButtonTypes.PrimaryBorderless:
      case ButtonTypes.PrimaryBorderless2:
      case ButtonTypes.PrimaryBorderless3:
        return 'text-primary text-sm font-bold';
      case ButtonTypes.SecondaryOutlined:
      case ButtonTypes.SecondaryBorderless:
        return 'text-secondary text-sm font-bold';
      case ButtonTypes.ErrorBorderless:
        return 'text-error text-sm font-bold';
      case ButtonTypes.OutlinedNeutral:
        return 'text-onSurface text-sm font-bold';
      default:
        return 'text-surface text-sm font-bold';
    }
  };

  const _getButtonSizeClassName = (): string => {
    return size === ButtonSizes.Small ? 'h-11' : 'h-12';
  };

  return (
    <button
      className={`${_getButtonBackgroundClassName()} ${_getButtonBorderClassName()} ${_getButtonTextClassName()} ${
        disabled ? 'opacity-25' : ''
      } ${_getButtonSizeClassName()} button ${extraClassName}`}
      {...passProps}
      onClick={_handleClick}
    >
      {!!leftIcon && leftIcon}
      {loading ? (
        <img src={loadingIcon} className="w-6 h-6" alt={'loading'} />
      ) : (
        text
      )}
      {!!rightIcon && rightIcon}
    </button>
  );
};

export default Button;
